import React, { useState } from 'react';
import { ArrowLeft, ChevronRight, CircleCheck, KeyRound, Mail, ShieldCheck, TriangleAlert } from 'lucide-react';
import { AuthHeading } from './AuthShell.jsx';
import { OtpCodeCard, useOtp } from '../components/Otp.jsx';
import { PhraseInput } from '../components/Phrase.jsx';
import { PasswordStrength, useToast } from '../components/ui.jsx';
import { maskEmail, mmss, OTP_LIMITS } from '../lib/otp.js';
import { passwordScore } from '../lib/recovery.js';
import { useLocalKeystore } from '../lib/keystore.js';

/**
 * Forgot password. The server authorizes the mailbox-password change ONLY with a
 * verified OTP token whose target is the account's registered recovery email -
 * so every reset path must send a real `token` (never an unverifiable phraseProof).
 *  - recovery email code  → proves ownership via the email OTP; CANNOT unseal keys
 *    (mail sealed before today stays locked until the phrase is entered later)
 *  - 12-word phrase       → unseals private keys locally and re-seals them with the
 *    new password, but the password change itself is still authorized by an email OTP
 */
export function Forgot({ api, initialEmail = '', go }) {
  const flash = useToast();
  const keystore = useLocalKeystore();
  const [step, setStep] = useState('method'); // method | otp | phrase | reset | done
  const [email, setEmail] = useState(initialEmail);
  const [method, setMethod] = useState(null);
  const [msg, setMsg] = useState('');
  const [words, setWords] = useState(Array(12).fill(''));
  const [bad, setBad] = useState(-1);
  const [phraseAttempts, setPhraseAttempts] = useState(0);
  const [phraseLock, setPhraseLock] = useState(0);
  const [phraseMsg, setPhraseMsg] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [account, setAccount] = useState(null); // { recoveryEmail } from server lookup
  const otp = useOtp(api, 'forgot');
  const score = passwordScore(pw);
  const valid = score >= 3 && pw === pw2;

  const lookup = async () => {
    try { const a = await api.settings(email.trim()); setAccount(a); return a; } catch { setMsg('No account found with that address.'); return null; }
  };
  const useEmail = async () => {
    const a = account || await lookup(); if (!a) return;
    if (!a.recoveryEmail) { setMsg('This account has no recovery email. Use your recovery phrase instead.'); return; }
    setMethod('email'); setMsg(''); otp.reset(a.recoveryEmail); setStep('otp'); setTimeout(() => otp.send(a.recoveryEmail), 0);
  };
  const usePhrase = async () => {
    const a = account || await lookup(); if (!a) return;
    // The password change is authorized by an email OTP, so the phrase path still
    // requires a recovery email on file. Be honest when there isn't one.
    if (!a.recoveryEmail) { setMsg('This account has no recovery email, so the password cannot be reset here. Add a recovery email while signed in, or contact your administrator.'); return; }
    setMethod('phrase'); setMsg(''); setStep('phrase');
  };
  const verifyOtp = async () => { if (await otp.verify()) setStep('reset'); };
  const verifyPhrase = async () => {
    if (phraseLock > 0) return;
    // Phrase never leaves the device: it unlocks the locally stored sealed key bundle.
    const r = await keystore.checkPhrase(email, words).catch(() => ({ ok: false, mismatch: 0 }));
    // Phrase is good locally, but the mailbox-password change still needs an email
    // OTP - send the code and move to the shared OTP step.
    if (r.ok) { setPhraseMsg(''); otp.reset(account.recoveryEmail); setStep('otp'); setTimeout(() => otp.send(account.recoveryEmail), 0); return; }
    const idx = r.mismatch;
    const attempts = phraseAttempts + 1;
    if (attempts >= OTP_LIMITS.maxAttempts) { setPhraseAttempts(0); setPhraseLock(OTP_LIMITS.attemptLock); setPhraseMsg(`Too many attempts. Try again in ${mmss(OTP_LIMITS.attemptLock)}`); const t = setInterval(() => setPhraseLock((n) => { if (n <= 1) { clearInterval(t); setPhraseMsg(''); return 0; } setPhraseMsg(`Too many attempts. Try again in ${mmss(n - 1)}`); return n - 1; }), 1000); }
    else { setPhraseAttempts(attempts); setBad(idx); setPhraseMsg(words[idx] ? `Phrase does not match · ${OTP_LIMITS.maxAttempts - attempts} attempts left` : `Word ${idx + 1} is missing`); }
  };
  const reset = async (e) => {
    e.preventDefault(); if (!valid) { flash('Choose a stronger password that matches'); return; }
    if (!otp.token) { flash('Verify the code sent to your recovery email first'); return; }
    try {
      // The phrase path also re-seals the local private keys with the new password,
      // but the server password change is always authorized by the verified OTP token.
      if (method === 'phrase') await keystore.resealWithPassword(email, pw);
      await api.resetPassword({ email: email.trim(), token: otp.token, newPassword: pw });
      setStep('done');
    } catch (err) { flash(err.message || 'Reset failed'); }
  };

  const T = { method: ['Reset your password', 'Choose how to prove it is you.'], otp: ['Check your recovery email', `We sent a 6-digit code to ${maskEmail(account?.recoveryEmail)}.`], phrase: ['Enter your recovery phrase', 'Type the 12 words in order. Paste works too.'], reset: ['Choose a new password', 'At least 12 characters with a mix of cases, numbers and symbols.'], done: ['You are back in', ''] }[step];

  return (
    <div className="km-auth-form" style={{ width: 440 }}>
      <a href="#login" onClick={(e) => { e.preventDefault(); go('login'); }} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, alignSelf: 'flex-start' }}><ArrowLeft size={14} />Back to sign in</a>
      <AuthHeading title={T[0]} sub={T[1]} />

      {step === 'method' && (<>
        <label className="km-field">Account email<input className="km-input" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setAccount(null); setMsg(''); }} /></label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <MethodCard icon={<Mail size={20} color="var(--km-accent)" />} title="Code to recovery email" sub={account ? (account.recoveryEmail ? `Send a code to ${maskEmail(account.recoveryEmail)}` : 'No recovery email on file for this account') : 'We will check which options this account has'} onClick={useEmail} />
          <MethodCard icon={<KeyRound size={20} color="var(--km-accent)" />} title="12-word recovery phrase" sub="Also re-seals your encrypted mail with the new password" onClick={usePhrase} />
        </div>
        {msg && <span style={{ fontSize: 13, color: 'var(--km-warn)' }}>{msg}</span>}
      </>)}

      {step === 'otp' && <OtpCodeCard otp={otp} onVerify={verifyOtp} />}

      {step === 'phrase' && (
        <div className="km-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <PhraseInput words={words} badIndex={bad} onChange={(w) => { setWords(w); setBad(-1); setPhraseMsg(''); }} />
          <span style={{ fontSize: 12.5, color: bad >= 0 || phraseLock ? 'var(--km-bad)' : 'var(--km-muted)' }}>{phraseMsg}</span>
          <button className="km-btn" style={{ padding: 11 }} onClick={verifyPhrase} disabled={phraseLock > 0}>Verify phrase</button>
        </div>
      )}

      {step === 'reset' && (
        <form onSubmit={reset} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label className="km-field">New password<input className="km-input" type="password" autoComplete="new-password" placeholder="At least 12 characters" value={pw} onChange={(e) => setPw(e.target.value)} /><PasswordStrength score={score} pw={pw} /></label>
          <label className="km-field">Confirm password<input className={`km-input ${pw2 && pw2 !== pw ? 'bad' : ''}`} type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></label>
          <div className="km-card" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', fontSize: 13, lineHeight: 1.5, color: 'var(--km-body)' }}>
            {method === 'phrase' ? <ShieldCheck size={16} color="var(--km-ok)" style={{ flex: 'none', marginTop: 1 }} /> : <TriangleAlert size={16} color="var(--km-warn)" style={{ flex: 'none', marginTop: 1 }} />}
            <span>{method === 'phrase' ? 'Your recovery phrase unseals your private keys, so they will be re-sealed with the new password. All existing mail stays readable.' : 'A recovery email proves ownership of the account but cannot unseal your private keys. You will regain access, but mail encrypted before today stays locked until you enter your recovery phrase in Settings.'}</span>
          </div>
          <button type="submit" className="km-btn km-btn-lg" aria-disabled={!valid}>Reset password</button>
        </form>
      )}

      {step === 'done' && (<>
        <div className="km-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', padding: '28px 20px' }}>
          <CircleCheck size={34} color="var(--km-ok)" /><span style={{ fontSize: 15, fontWeight: 600 }}>Password updated</span>
          <span className="km-muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>{method === 'phrase' ? 'Your keys are re-sealed. Sign in with the new password.' : 'Sign in with the new password. Open Settings › Security to unlock older encrypted mail with your recovery phrase.'}</span>
        </div>
        <button className="km-btn km-btn-lg" onClick={() => { go('login', { email }); flash('Password updated · all other sessions signed out'); }}>Sign in</button>
      </>)}
    </div>
  );
}

function MethodCard({ icon, title, sub, onClick }) {
  return <button type="button" className="km-method" onClick={onClick}>{icon}<span><b>{title}</b><small>{sub}</small></span><ChevronRight size={16} color="var(--km-faint)" /></button>;
}
